import { forwardRef, type ButtonHTMLAttributes, type ComponentType, type ReactNode, type SVGProps } from "react";
import { cn } from "../../lib/cn";
import { Spinner } from "./Spinner";
import { Tooltip } from "./Tooltip";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary: "bg-primary text-fg-inverse hover:bg-primary-hover disabled:opacity-50",
  secondary: "bg-surface-2 text-fg hover:bg-surface-3 disabled:opacity-50",
  outline: "border border-line-strong text-fg hover:bg-surface disabled:opacity-50",
  ghost: "text-fg-2 hover:bg-surface hover:text-fg disabled:opacity-50",
  danger: "bg-danger text-white hover:brightness-110 disabled:opacity-50",
};
const sizes: Record<Size, string> = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-9 px-3.5 text-sm gap-2",
  lg: "h-10 px-4 text-base gap-2",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon: Icon, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-md font-medium transition-[background-color,color,opacity,filter] duration-150 active:translate-y-px",
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : Icon ? <Icon className="icon size-[18px]" aria-hidden /> : null}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  size?: "sm" | "md" | "lg";
  active?: boolean;
  tooltip?: boolean;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  shortcut?: string;
  tone?: "default" | "danger";
  badge?: ReactNode;
}

const iconSizes = { sm: "size-8", md: "size-9", lg: "size-10" };

/** Icon-only control with an accessible name and a tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon: Icon, size = "md", active, tooltip = true, tooltipSide, shortcut, tone = "default", badge, className, type = "button", ...rest },
  ref,
) {
  const btn = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={active === undefined ? undefined : active}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center rounded-md transition-colors duration-150 disabled:opacity-40",
        iconSizes[size],
        tone === "danger" ? "text-fg-2 hover:bg-danger-soft hover:text-danger" : "text-fg-2 hover:bg-surface hover:text-fg",
        active && "bg-surface-2 text-fg",
        className,
      )}
      {...rest}
    >
      <Icon className={cn("icon", size === "sm" ? "size-[18px]" : "size-5")} aria-hidden />
      {badge}
    </button>
  );
  return tooltip ? (
    <Tooltip label={label} side={tooltipSide} shortcut={shortcut}>
      {btn}
    </Tooltip>
  ) : (
    btn
  );
});
